import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '@/services/inAppNotificationService';
import { notificationTarget } from '@/lib/social/notificationTarget';
import { useNotifications } from '@/hooks/useNotifications';
import { useAdminRole } from '@/hooks/useAdminRole';
import { formatDistanceToNow } from 'date-fns';
import { 
  MessageSquare, 
  AtSign, 
  Gift, 
  Trophy, 
  UserPlus, 
  Mail,
  Flame,
  Target,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { springSnappy } from '@/lib/motion/presets';

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const NOTIFICATION_ICONS = {
  thread_reply: MessageSquare,
  mention: AtSign,
  xp_gift: Gift,
  badge_earned: Trophy,
  connection_request: UserPlus,
  dm_message: Mail,
  streak_milestone: Flame,
  weekly_goal_met: Target,
  thread_featured: Star
};

const NOTIFICATION_COLORS = {
  thread_reply: 'text-blue-500',
  mention: 'text-purple-500',
  xp_gift: 'text-amber-500',
  badge_earned: 'text-yellow-500',
  connection_request: 'text-pink-500',
  dm_message: 'text-green-500',
  streak_milestone: 'text-orange-500',
  weekly_goal_met: 'text-emerald-500',
  thread_featured: 'text-indigo-500'
};

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onClose 
}) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();
  const { isAdmin } = useAdminRole();
  const Icon = NOTIFICATION_ICONS[notification.type] || MessageSquare;
  const iconColor = NOTIFICATION_COLORS[notification.type] || 'text-foreground';

  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification.id);
    }

    const target = notificationTarget(notification);
    const isSocial = /^\/(social|commons|community|mail)\b/.test(target);
    if (!isSocial || isAdmin) {
      navigate(target);
    }

    onClose();
  };

  return (
    <motion.button
      onClick={handleClick}
      whileTap={{ scale: 0.985 }}
      transition={springSnappy}
      className={cn(
        "w-full px-4 py-3 text-left hover:bg-accent/50 transition-colors relative",
        !notification.read && "bg-accent/30"
      )}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={cn("flex-shrink-0 mt-0.5", iconColor)}>
          <Icon className="h-5 w-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm mb-0.5 line-clamp-1">
            {notification.title}
          </p>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </p>
        </div>

        {/* Unread indicator */}
        {!notification.read && (
          <div className="flex-shrink-0">
            <div className="h-2 w-2 rounded-full bg-blue-500"></div>
          </div>
        )}
      </div>
    </motion.button>
  );
};
